import { useState } from "react";
import { App, Button, Card, Segmented, Space } from "antd";
import { useTranslation } from "react-i18next";
import type { NotificationDto } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useNotifications, useRunScheduler } from "../../api/admin";
import { Pill, fmtDT } from "../pills";
import { ResponsiveList } from "../ResponsiveList";
import { MailOutboxView } from "./MailOutboxView";
import { NotificationSettings } from "./NotificationSettings";
import { Sensitive } from "../Sensitive";

export function NotificationsTab() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [view, setView] = useState<"cfg" | "reg" | "out">("cfg");
  const q = useNotifications();
  const run = useRunScheduler();
  const go = async () => {
    try {
      await run.mutateAsync();
      void message.success(t("a_schedulerDone"));
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
  };
  return (
    <Card
      title={t("a_notifications")}
      styles={{ body: { padding: 0 } }}
      extra={
        <Space wrap>
          <Segmented size="small" value={view} onChange={(v) => setView(v as "cfg" | "reg" | "out")} options={[{ label: t("n_tabSettings"), value: "cfg" }, { label: t("c_registry"), value: "reg" }, { label: t("c_outbox"), value: "out" }]} />
          {view === "reg" && <Button loading={run.isPending} onClick={go}>{t("runNow")}</Button>}
        </Space>
      }
    >
      {view === "cfg" ? <NotificationSettings /> : view === "out" ? <MailOutboxView /> : <>
      <div style={{ padding: "12px 16px", fontSize: 13, opacity: 0.75 }}>{t("a_notifHelp")}</div>
      <ResponsiveList<NotificationDto>
        size="small"
        rowKey="id"
        loading={q.isLoading}
        dataSource={q.data ?? []}
        pageSize={20}
        emptyText={t("none")}
        tableProps={{ expandable: { expandedRowRender: (n) => <pre data-sensitive style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>{n.body}</pre> } }}
        card={{
          code: (n) => fmtDT(n.createdAt),
          title: (n) => n.subject,
          subtitle: (n) => <Sensitive>{n.to.map((x) => x.name || x.email).join(", ")}</Sensitive>,
          tags: (n) => <Pill tone="info">{n.kind}</Pill>,
          footer: (n) => (
            <details>
              <summary style={{ cursor: "pointer", fontSize: 13 }}>{t("detail")}</summary>
              <pre data-sensitive style={{ whiteSpace: "pre-wrap", margin: "6px 0 0", fontFamily: "inherit", fontSize: 13, overflowWrap: "anywhere" }}>{n.body}</pre>
            </details>
          ),
        }}
        columns={[
          { title: t("date"), dataIndex: "createdAt", render: (v: string) => fmtDT(v) },
          { title: t("kind"), dataIndex: "kind", render: (v: string) => <Pill tone="info">{v}</Pill> },
          { title: t("recipients"), dataIndex: "to", render: (v: NotificationDto["to"]) => <Sensitive>{v.map((x) => x.name || x.email).join(", ")}</Sensitive> },
          { title: t("subject"), dataIndex: "subject" },
        ]}
      />
      </>}
    </Card>
  );
}
